import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/apiService';
import TenantFinancePanels, { FINANCE_PANELS, type FinancePanelId } from '../components/finance/TenantFinancePanels';
import { Skeleton, SkeletonRows } from '../components/Skeleton';
import { getIcon } from '../services/iconRegistry';
import { Tenant, PaymentEvent, PricePlan } from '../types';
import {
  ArrowLeft, RefreshCw, Shield, KeyRound, Send, History,
  MessageSquare, Mail, Sparkles, X, AlertTriangle,
  LayoutDashboard, ChevronRight, ChevronDown, Building2,
  UserCog, Zap, Activity, Eye, DollarSign,
  Lock, Unlock, FileText, Copy, Check, Headphones, Package, Tag, Box, MapPin,
  Monitor, Tv, Tablet
} from 'lucide-react';
import JourneyTimeline from './inventory/JourneyTimeline';
import PosTerminalTab from './pos/PosTerminalTab';
import AdvertisingTab from './advertising/AdvertisingTab';
import KiosksTab from './kiosks/KiosksTab';
import TenantPosAiAgentsTab from '../components/credits/TenantPosAiAgentsTab';
import { pushNotify } from '../services/websocketService';
import ApproveLocationForm from '../components/locations/ApproveLocationForm';
import BilledVsEnabledPanel from '../components/tenant/BilledVsEnabledPanel';
import LocationsBillingPanel from '../components/tenant/LocationsBillingPanel';
import { applyTheme } from '../services/theme';
import { billingApi } from '../services/billingApi';
import { toastService } from '../services/toastService';
import { issuesApi, AdminIssue, AdminIssueComment } from '../services/issuesApi';
import { tenantPackageRows } from '../services/packageTiers';

// Helpers
const formatMoney = (amount?: number | null, currency = 'usd') => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
};

const normalizeTimestamp = (ts?: number | null): number | null => {
  if (!ts) return null;
  if (ts < 946684800000) return ts * 1000;
  return ts;
};

const formatDate = (ts?: number | null): string => {
  const normalized = normalizeTimestamp(ts);
  if (!normalized) return 'N/A';
  return new Date(normalized).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (ts?: number | null): string => {
  const normalized = normalizeTimestamp(ts);
  if (!normalized) return 'N/A';
  return new Date(normalized).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ---------------------------------------------------------------------------
// Support tab — unified support-service issues (2026-08-26). Mirrors the
// kind/status vocabulary and timeline mapping from SuperAdminSupportTickets.tsx
// (the global Support Center) so operators see the same language here.
// ---------------------------------------------------------------------------
const SUPPORT_KIND_EMOJI: Record<string, string> = {
  issue: '🐞', device: '🖥️', billing: '💳', question: '❓', contact: '✉️',
};
const SUPPORT_KIND_LABEL: Record<string, string> = {
  issue: 'App issue', device: 'Device problem', billing: 'Billing', question: 'Question', contact: 'Contact',
};
const SUPPORT_STATUS_META: Record<string, { label: string; cls: string }> = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700' },
  awaiting_retest: { label: 'Ready to re-test', cls: 'bg-emerald-100 text-emerald-700' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-50 text-emerald-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};
const SUPPORT_OPEN_STATUSES = new Set(['new', 'in_progress', 'awaiting_retest']);

const supportTimeAgo = (ts: number): string => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

type SupportTimelineEvent =
  | { kind: 'created'; ts: number }
  | { kind: 'status'; ts: number; text: string }
  | { kind: 'call'; ts: number; comment: AdminIssueComment }
  | { kind: 'bubble'; ts: number; comment: AdminIssueComment; side: 'left' | 'right' };

const buildSupportTimeline = (issue: AdminIssue, comments: AdminIssueComment[]): SupportTimelineEvent[] => {
  const events: SupportTimelineEvent[] = [{ kind: 'created', ts: issue.createdAt }];
  [...comments].sort((a, b) => a.createdAt - b.createdAt).forEach((c) => {
    if (c.kind === 'status_change' || c.authorRole === 'system') {
      events.push({ kind: 'status', ts: c.createdAt, text: c.body });
    } else if (c.kind === 'phone_call') {
      events.push({ kind: 'call', ts: c.createdAt, comment: c });
    } else {
      events.push({ kind: 'bubble', ts: c.createdAt, comment: c, side: c.authorRole === 'reporter' ? 'left' : 'right' });
    }
  });
  return events.sort((a, b) => a.ts - b.ts);
};

// Types
type MainTab = 'overview' | 'finance' | 'packages' | 'support' | 'admin' | 'locations' | 'pos' | 'pos-ai' | 'advertising' | 'kiosks' | 'history';

const VALID_TABS: MainTab[] = [
  'overview', 'finance', 'packages', 'support', 'locations', 'admin', 'pos', 'pos-ai', 'advertising', 'kiosks',
];

/**
 * `?tab=billing` predates the Finance consolidation and is bookmarked. It keeps
 * resolving — to Finance, on the History panel, which is where its payment list
 * went. Silently dropping a link operators use would be worse than the tidy-up.
 */
const resolveTab = (raw: string | null): MainTab => {
  if (raw === 'billing') return 'finance';
  return raw && VALID_TABS.includes(raw as MainTab) ? (raw as MainTab) : 'overview';
};

/** `?panel=` picks the Finance sub-panel; a `?tab=billing` link opens History. */
const resolvePanel = (params: URLSearchParams): FinancePanelId => {
  const raw = params.get('panel');
  if (raw && FINANCE_PANELS.includes(raw as FinancePanelId)) return raw as FinancePanelId;
  return params.get('tab') === 'billing' ? 'history' : 'plan';
};

interface TenantPackageInfo {
  tenantId: string;
  packageId: string;
  status: string;
  assignedAt: number;
  assignedBy: string;
  canceledAt?: number;
}

interface PackageDetail {
  id: string;
  name: string;
  description: string;
  features: string[];
  isCore?: boolean;
  icon?: string;
  colorType?: 'solid' | 'gradient';
  color?: string;
  gradientFrom?: string;
  gradientTo?: string;
  basePrice?: number;
  // Package tiers (optional; present only on rows that belong to a family)
  familyId?: string;
  tierRank?: number;
  role?: string;
  tierLabel?: string;
}

/**
 * Skeleton-first placeholder mirroring the real page layout (black header,
 * tab rail, stone content with white cards) so the first paint is instant
 * and nothing jumps when the tenant record lands.
 */
const TenantProfileSkeleton: React.FC = () => (
  <div className="min-h-screen bg-black -m-3 md:-m-6">
    <header className="relative bg-black px-4 md:px-6 py-4">
      <RubyLoader variant="overlay" size={36} />
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <Skeleton className="h-11 w-11 rounded-xl shrink-0" />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-4 w-16 rounded-full" />
            </div>
            <Skeleton className="h-3 w-56" />
            <Skeleton className="h-2.5 w-36" />
          </div>
        </div>
        <div className="hidden lg:flex items-center gap-6 px-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-10 w-28 rounded-xl hidden sm:block" />
          <Skeleton className="h-10 w-10 rounded-xl" />
        </div>
      </div>
      <div className="flex items-center gap-1 mt-4 overflow-hidden">
        <Skeleton className="h-10 w-28 rounded-xl shrink-0" />
        <Skeleton className="h-10 w-24 rounded-xl shrink-0" />
        <Skeleton className="h-10 w-28 rounded-xl shrink-0" />
        <Skeleton className="h-10 w-24 rounded-xl shrink-0" />
        <Skeleton className="h-10 w-28 rounded-xl shrink-0" />
        <Skeleton className="h-10 w-20 rounded-xl shrink-0" />
        <Skeleton className="h-10 w-16 rounded-xl shrink-0" />
      </div>
    </header>
    <div className="h-1" style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)' }} />
    <div className="bg-stone-100 min-h-screen">
      <main className="p-4 md:p-6 lg:p-8 w-full max-w-[90%] mx-auto space-y-5">
        <div className="relative bg-white rounded-2xl border border-stone-200 p-5">
          <RubyLoader variant="overlay" size={24} />
          <Skeleton variant="light" className="h-4 w-32 mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="light" className="h-11 rounded-xl" />
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <Skeleton variant="light" className="h-4 w-40 mb-4" />
          <SkeletonRows variant="light" count={4} />
        </div>
      </main>
    </div>
  </div>
);

const TenantProfile: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  // Data states
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [billingEvents, setBillingEvents] = useState<PaymentEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<boolean>(false);
  const [supportIssues, setSupportIssues] = useState<AdminIssue[]>([]);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [issueDetail, setIssueDetail] = useState<{ issue: AdminIssue; comments: AdminIssueComment[] } | null>(null);
  const [issueDetailLoading, setIssueDetailLoading] = useState(false);
  const [supportReplyText, setSupportReplyText] = useState('');
  const [supportInternalNote, setSupportInternalNote] = useState(false);
  const [supportPosting, setSupportPosting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [sendingWelcome, setSendingWelcome] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailExtraRecipients, setEmailExtraRecipients] = useState('');
  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [prices, setPrices] = useState<PricePlan[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [adminActivity, setAdminActivity] = useState<any[]>([]);

  // UI states
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * `billing` was its own tab until the Finance consolidation. It is still a
   * legal URL value — operators have `?tab=billing` bookmarked — and now
   * resolves to Finance, opened on the History panel, which is where its
   * payment list went.
   */
  const [activeTab, setActiveTabState] = useState<MainTab>(() => resolveTab(searchParams.get('tab')));
  const [financePanel, setFinancePanel] = useState<FinancePanelId>(() => resolvePanel(searchParams));
  // Wrap setActiveTab so every tab click pushes ?tab=<id> to the URL. Makes
  // tabs shareable / back-button-friendly. Browser back/forward syncs both
  // ways via the searchParams effect below.
  const setActiveTab = useCallback((tab: MainTab) => {
    setActiveTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      // `panel` only means anything on Finance; leaving it behind would make a
      // shared link say "Locations panel" while showing the Support tab.
      if (tab !== 'finance') next.delete('panel');
      return next;
    }, { replace: false });
  }, [setSearchParams]);
  const changeFinancePanel = useCallback((panel: FinancePanelId) => {
    setFinancePanel(panel);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'finance');
      next.set('panel', panel);
      return next;
    }, { replace: false });
  }, [setSearchParams]);
  // Browser back/forward → URL changes → tab follows.
  useEffect(() => {
    const fromUrl = resolveTab(searchParams.get('tab'));
    if (searchParams.get('tab') && fromUrl !== activeTab) setActiveTabState(fromUrl);
    const panelFromUrl = resolvePanel(searchParams);
    if (panelFromUrl !== financePanel) setFinancePanel(panelFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, activeTab]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['subscription', 'actions']));

  const [showModal, setShowModal] = useState<'reset' | 'welcome' | 'suspend' | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Suspension states
  const [suspending, setSuspending] = useState(false);

  // Package management states
  const [tenantPackages, setTenantPackages] = useState<TenantPackageInfo[]>([]);
  const [allPackages, setAllPackages] = useState<PackageDetail[]>([]);
  // Package tiers (2026-10-11): api.packageFamilies.list() returns EVERY
  // family, not just ones touching this tenant — used to match any tier of
  // a family, not just its one top package id.
  const [families, setFamilies] = useState<{ id: string; tierIds: string[]; topTierId?: string }[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [assigningPackage, setAssigningPackage] = useState(false);
  const [removingPackage, setRemovingPackage] = useState<string | null>(null);
  const [packageToggleConfirm, setPackageToggleConfirm] = useState<{
    packageId: string;
    packageName: string;
    action: 'enable' | 'disable';
  } | null>(null);
  const [togglingPackage, setTogglingPackage] = useState<string | null>(null);

  // Billed vs Enabled panel (Task 5): the tenant's active PlanAssignment's
  // planPackageIds — `null` means "not loaded yet" or "failed to load",
  // distinct from `[]` which means "loaded, tenant has no active assignment".
  const [billedPackageIds, setBilledPackageIds] = useState<string[] | null>(null);
  const [billedPackagesLoading, setBilledPackagesLoading] = useState(false);
  const [billedPackagesError, setBilledPackagesError] = useState<string | null>(null);

  // Locations & Billing (2026-09-07): the per-LOCATION billing side, read from
  // the drift detail — it already walks each location's subscription and now
  // carries the upcoming invoice with it. `null` means the read failed or has
  // not run, which the card renders as a fallback, never as "Free".
  const [driftLocations, setDriftLocations] = useState<any[] | null>(null);
  const [driftLocationsLoading, setDriftLocationsLoading] = useState(false);
  const [driftLocationsError, setDriftLocationsError] = useState<string | null>(null);

  // Location management states
  const [locationRequests, setLocationRequests] = useState<any[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationActionModal, setLocationActionModal] = useState<{type: string; request: any} | null>(null);
  const [locationModalInput, setLocationModalInput] = useState('');
  const [availableProducts, setAvailableProducts] = useState<Array<{ id: string; name: string; defaultPackages?: string[] }>>([]);
  const [canManagePackages, setCanManagePackages] = useState(true);

  // Plan & Packages (2026-08-26 spec): the tenant's deal drives the strip at
  // the top of the tab (packages included, per-package discounts, location
  // terms) and the location-billing card at the bottom of the same tab.
  const [tenantDeal, setTenantDeal] = useState<any | null>(null);
  const [reapplyingDeal, setReapplyingDeal] = useState(false);
  const [locationApproveModal, setLocationApproveModal] = useState<any | null>(null);

  // Support tab states
  const supportMessagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when the selected ticket's timeline changes
  useEffect(() => {
    supportMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [issueDetail]);

  // Apply dark theme
  useEffect(() => {
    applyTheme('dark');
  }, []);

  // Data loading — the page renders as soon as the tenant record lands;
  // billing/audit/activity and tickets load in parallel and stream into
  // their sections. Catches are attached at creation so a getTenant failure
  // can't leave these rejections unhandled.
  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      loadPrices();

      const secondary = Promise.all([
        api.superAdmin.getTenantBilling(tenantId),
        api.superAdmin.getTenantAuditLog(tenantId),
        api.superAdmin.getTenantAdminActivity(tenantId).catch(() => ({ events: [] }))
      ]).then(([bill, audit, activity]) => {
        setBillingEvents(Array.isArray(bill?.events) ? bill.events : []);
        setAuditLogs(Array.isArray(audit?.events) ? audit.events : []);
        setAdminActivity(Array.isArray(activity?.events) ? activity.events : []);
      }).catch((err: any) => err as Error);

      const ticketsLoad = loadSupportIssuesFor(tenantId).then(async (list) => {
        setSupportIssues(list);
        if (list[0]) {
          setSelectedIssueId(list[0].id);
          await loadIssueDetail(list[0].id);
        }
      }).catch((err: any) => err as Error);

      const t = await api.superAdmin.getTenant(tenantId);
      setTenant(t);

      const firstError = (await Promise.all([secondary, ticketsLoad])).find(Boolean);
      if (firstError) throw firstError;
    } catch (err: any) {
      setActionMessage(err?.message || 'Failed to load tenant profile');
    } finally {
      setLoading(false);
    }
  };

  // No status filter on the support-service /admin/issues endpoint unions only
  // the active statuses, so resolved/rejected are fetched with their own
  // explicit queries and merged client-side — same pattern used by the global
  // Support Center (SuperAdminSupportTickets.tsx), just scoped to this tenant.
  const loadSupportIssuesFor = async (tid: string): Promise<AdminIssue[]> => {
    const settled = await Promise.allSettled([
      issuesApi.list({ tenantId: tid, limit: 200 }),
      issuesApi.list({ tenantId: tid, status: 'resolved', limit: 100 }),
      issuesApi.list({ tenantId: tid, status: 'rejected', limit: 100 }),
    ]);
    const merged = new Map<string, AdminIssue>();
    let anyFailed = false;
    settled.forEach((r) => {
      if (r.status === 'fulfilled') {
        (r.value.issues || []).forEach((i) => merged.set(i.id, i));
      } else {
        anyFailed = true;
      }
    });
    if (anyFailed) toastService.error('Some support tickets failed to load — showing partial results');
    return Array.from(merged.values()).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  };

  const loadIssueDetail = async (id: string) => {
    setIssueDetailLoading(true);
    try {
      const res = await issuesApi.get(id);
      setIssueDetail(res);
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to load ticket');
      setIssueDetail(null);
    } finally {
      setIssueDetailLoading(false);
    }
  };

  const loadPrices = async () => {
    setPricesLoading(true);
    try {
      const res = await api.locationBilling.listPrices();
      const fetched = Array.isArray(res?.items) ? res.items : [];
      setPrices(fetched);
    } finally {
      setPricesLoading(false);
    }
  };

  // Load tenant packages and available products
  const loadTenantPackages = async () => {
    if (!tenantId) return;
    setPackagesLoading(true);
    console.log('[TenantProfile] loadTenantPackages called for tenant:', tenantId);
    try {
      // Load packages and products in parallel
      const [tenantPkgRes, productsRes, familiesRes] = await Promise.all([
        api.superAdmin.getTenantPackages(tenantId),
        api.packages.listProducts(),
        api.packageFamilies.list().catch(() => ({ families: [] })),
      ]);
      setFamilies(familiesRes.families || []);
      console.log('[TenantProfile] getTenantPackages response:', {
        tenantProductType: tenantPkgRes.tenant?.productType,
        packagesCount: tenantPkgRes.packages?.length,
        allPackagesCount: tenantPkgRes.allPackages?.length,
        canManagePackages: (tenantPkgRes as any).canManagePackages,
        allPackages: tenantPkgRes.allPackages?.map((p: any) => ({ id: p.id, name: p.name }))
      });
      setTenantPackages(tenantPkgRes.packages || []);
      // Use allPackages from the response (all active packages from PACKAGES_TABLE)
      setAllPackages(tenantPkgRes.allPackages || tenantPkgRes.packageDetails || []);
      // Set whether packages can be managed (tenant has a product assigned)
      setCanManagePackages((tenantPkgRes as any).canManagePackages !== false);
      // Set available products for the dropdown
      setAvailableProducts(productsRes.products || []);

      // The deal behind this tenant's dealCode (2026-08-26): drives the deal
      // strip and the location terms shown on this tab. Best-effort — a
      // lookup failure only hides deal detail, never the packages.
      const dealCode = String(tenantPkgRes.tenant?.dealCode || '').trim().toUpperCase();
      if (dealCode) {
        api.deals.list()
          .then((res) => {
            const match = (res.deals || []).find(
              (d: any) => String(d.code || '').trim().toUpperCase() === dealCode,
            );
            setTenantDeal(match || null);
          })
          .catch(() => setTenantDeal(null));
      } else {
        setTenantDeal(null);
      }
    } catch (err) {
      console.error('Failed to load packages:', err);
      setTenantPackages([]);
      setAllPackages([]);
      setAvailableProducts([]);
      setFamilies([]);
    } finally {
      setPackagesLoading(false);
    }
  };

  /**
   * Runs after a successful package entitlement write — never before, and
   * never able to undo or obscure it (D8: `assignTenantPackage` /
   * `removeTenantPackage` already committed `tenant.enabledPackages` by the
   * time this is called). Any failure here is caught internally, never
   * re-thrown, so it can never make an entitlement change look like it failed.
   *
   * A `skipped: 'no-plan-assignment'` result is the NORMAL state for every
   * tenant today (D12) and is shown as information, not an error. A sync
   * failure is recorded server-side as `tenant.billingSyncPending` — this
   * toast is just the immediate signal; the persistent banner on the Finance
   * tab is what keeps it visible after the toast disappears.
   */
  const syncBillingAfterPackageChange = async () => {
    if (!tenantId) return;
    try {
      const result = await billingApi.syncSubscription(tenantId);
      if (result.skipped === 'no-plan-assignment') {
        toastService.info('Package saved. This tenant bills on legacy flat pricing (no plan assignment yet) — billing is unaffected.');
      } else if (result.errors.length) {
        toastService.warning(
          'Package change saved, but billing did not update. The tenant already has the feature — use "Sync billing" on the Finance tab to retry.',
          10000,
        );
      } else {
        const changed = result.added + result.removed + result.repriced;
        toastService.success(
          changed > 0
            ? `Billing synced — ${result.added} added, ${result.removed} removed${result.repriced ? `, ${result.repriced} repriced` : ''}.`
            : "Billing already matched this tenant's plan.",
        );
      }
      // Merge the outcome into local tenant state so the Finance tab's
      // billingSyncPending banner is correct without another full reload.
      // Skip touches nothing server-side (D12), so nothing is merged for it.
      if (!result.skipped) {
        setTenant((prev) => (prev ? {
          ...prev,
          billingSyncPending: result.errors.length > 0,
          billingSyncError: result.errors[0]?.reason,
          billingSyncAt: Date.now(),
        } : prev));
      }
    } catch (err: any) {
      const message = err?.message || 'unknown error';
      toastService.warning(
        `Package change saved, but the billing sync could not run (${message}). Use "Sync billing" on the Finance tab to retry.`,
        10000,
      );
      // A throw here often means the request never reached billing-service at
      // all (network error, unexpected status), so there is nothing for the
      // server to have persisted either — the toast above is the ONLY signal,
      // and it is gone in 10s. Without this, the operator is left with no
      // signal at all, which is exactly the failure mode this task exists to
      // prevent. Flag it locally so the Finance tab's persistent banner still
      // shows; the next successful sync (button or another package change)
      // clears it exactly as a server-recorded failure would.
      setTenant((prev) => (prev ? {
        ...prev,
        billingSyncPending: true,
        billingSyncError: `billing sync could not run: ${message}`,
        billingSyncAt: Date.now(),
      } : prev));
    }
  };

  // Assign package to tenant
  const handleAssignPackage = async (packageId: string) => {
    if (!tenantId) return;
    setAssigningPackage(true);
    try {
      await api.superAdmin.assignTenantPackage(tenantId, packageId);
      setActionMessage('Package assigned successfully');
      await loadTenantPackages();
      await load(); // Refresh tenant to update enabledPackages
      pushNotify('config:package', { tenantId });
      await syncBillingAfterPackageChange();
    } catch (err: any) {
      setActionMessage(err?.message || 'Failed to assign package');
    } finally {
      setAssigningPackage(false);
    }
  };

  // Remove package from tenant
  const handleRemovePackage = async (packageId: string) => {
    if (!tenantId) return;
    setRemovingPackage(packageId);
    try {
      await api.superAdmin.removeTenantPackage(tenantId, packageId);
      setActionMessage('Package removed successfully');
      await loadTenantPackages();
      await load(); // Refresh tenant to update enabledPackages
      pushNotify('config:package', { tenantId });
      await syncBillingAfterPackageChange();
    } catch (err: any) {
      setActionMessage(err?.message || 'Failed to remove package');
    } finally {
      setRemovingPackage(null);
    }
  };

  // Toggle package (enable/disable) with confirmation
  const handleTogglePackage = async () => {
    if (!tenantId || !packageToggleConfirm) return;
    const { packageId, action } = packageToggleConfirm;
    setTogglingPackage(packageId);
    try {
      if (action === 'enable') {
        await api.superAdmin.assignTenantPackage(tenantId, packageId);
        setActionMessage('Package enabled successfully');
      } else {
        await api.superAdmin.removeTenantPackage(tenantId, packageId);
        setActionMessage('Package disabled successfully');
      }
      await loadTenantPackages();
      await load(); // Refresh tenant to update enabledPackages
      pushNotify('config:package', { tenantId });
      await syncBillingAfterPackageChange();
    } catch (err: any) {
      setActionMessage(err?.message || `Failed to ${action} package`);
    } finally {
      setTogglingPackage(null);
      setPackageToggleConfirm(null);
    }
  };

  /**
   * Re-apply the deal's packages (2026-08-26): enables every package the deal
   * includes that the tenant does not have — the projection deal activation
   * historically never did. Additive only: it never REMOVES a package the
   * operator granted outside the deal. Each assign also rewrites
   * allowedFeatures server-side, then billing syncs once at the end.
   */
  const handleReapplyDealPackages = async () => {
    if (!tenantId || !tenantDeal?.packageIds?.length) return;
    const enabled = new Set(tenantPackages.filter((p) => p.status === 'active').map((p) => p.packageId));
    const missing: string[] = tenantDeal.packageIds.filter((id: string) => !enabled.has(id));
    if (!missing.length) {
      toastService.info('All of this deal\'s packages are already enabled.');
      return;
    }
    setReapplyingDeal(true);
    try {
      for (const packageId of missing) {
        await api.superAdmin.assignTenantPackage(tenantId, packageId);
      }
      toastService.success(`Enabled ${missing.length} deal package${missing.length > 1 ? 's' : ''}.`);
      await loadTenantPackages();
      await load();
      pushNotify('config:package', { tenantId });
      await syncBillingAfterPackageChange();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to apply deal packages');
      await loadTenantPackages();
    } finally {
      setReapplyingDeal(false);
    }
  };

  useEffect(() => { load(); loadTenantPackages(); }, [tenantId]);

  // Reload packages when tenant's product type changes
  // This ensures Package Management section refreshes after product type update
  // Uses a ref to track previous value and only reload on actual changes
  const prevProductTypeRef = React.useRef<string | null | undefined>(undefined);
  useEffect(() => {
    // Only reload if tenant exists and productType actually changed (not just initial load)
    if (tenant && prevProductTypeRef.current !== undefined && prevProductTypeRef.current !== tenant.productType) {
      console.log('[TenantProfile] Product type changed from', prevProductTypeRef.current, 'to', tenant.productType, '- reloading packages');
      loadTenantPackages();
    }
    prevProductTypeRef.current = tenant?.productType;
  }, [tenant?.productType]);

  // Location requests
  const fetchLocationRequests = async () => {
    if (!tenantId) return;
    setLocationsLoading(true);
    try {
      const data = await api.locationBilling.listTenantRequests(tenantId);
      setLocationRequests((data as any)?.requests || []);
    } catch (e) {
      console.error('Failed to fetch location requests:', e);
    } finally {
      setLocationsLoading(false);
    }
  };

  // Billed vs Enabled panel (Task 5): the SAME client (`billingApi`) the
  // Finance tab's PlanAssignmentBuilder uses — `?status=active` is the only
  // correct read of the current plan (see PlanAssignmentBuilder.tsx's `load`).
  const loadBilledPackages = async () => {
    if (!tenantId) return;
    setBilledPackagesLoading(true);
    setBilledPackagesError(null);
    try {
      const res = await billingApi.getActivePlanAssignment(tenantId);
      const row = (res.items || [])[0];
      setBilledPackageIds(row ? Array.from(new Set(row.planPackageIds || [])) : []);
    } catch (err: any) {
      // "Could not read" and "there is none" are different facts — never
      // collapse a failed fetch into an empty billed set.
      setBilledPackageIds(null);
      setBilledPackagesError(err?.message || 'The active plan assignment could not be read.');
    } finally {
      setBilledPackagesLoading(false);
    }
  };

  /**
   * The per-location billing side for the Locations & Billing card.
   *
   * `GET /billing/drift/{tenantId}` reads Stripe LIVE, which is why it is
   * called when an operator opens the tab and never as part of the list. Each
   * location comes back with its subscription and its UPCOMING INVOICE — the
   * only honest answer to what a store costs, because promotions live on the
   * subscription schedule's phases and an item sum strips every one of them out.
   */
  const loadDriftLocations = async () => {
    if (!tenantId) return;
    setDriftLocationsLoading(true);
    setDriftLocationsError(null);
    try {
      const res = await billingApi.getBillingDriftDetail(tenantId);
      setDriftLocations(res.locations || []);
    } catch (err: any) {
      // "Could not read" is not "there are no locations". Collapsing the two
      // is how the old card came to report billed stores as free.
      setDriftLocations(null);
      setDriftLocationsError(err?.message || 'The billing side could not be read.');
    } finally {
      setDriftLocationsLoading(false);
    }
  };

  useEffect(() => {
    if ((activeTab === 'locations' || activeTab === 'finance' || activeTab === 'packages') && tenantId) {
      fetchLocationRequests();
      if (activeTab === 'packages' && driftLocations === null && !driftLocationsLoading && !driftLocationsError) {
        loadDriftLocations();
      }
      if (prices.length === 0 && !pricesLoading) {
        setPricesLoading(true);
        api.locationBilling.listPrices().then((data: any) => {
          setPrices(data?.items || []);
        }).catch(() => {}).finally(() => setPricesLoading(false));
      }
      if (activeTab === 'packages' && billedPackageIds === null && !billedPackagesLoading) {
        loadBilledPackages();
      }
    }
  }, [activeTab, tenantId]);

  const handleImpersonate = async () => {
    if (!tenantId) return;
    setImpersonating(true);
    try {
      const creds = await api.superAdmin.impersonateTenant(tenantId);
      const url = `${window.location.origin}${window.location.pathname}#/impersonate?tenantId=${encodeURIComponent(tenantId)}&token=${encodeURIComponent((creds as any).impersonationToken || (creds as any).token || '')}`;
      window.open(url, '_blank', 'noopener');
      setActionMessage('Opening owner session in new tab...');
    } catch (err: any) {
      setActionMessage(err?.message || 'Failed to impersonate');
    } finally {
      setImpersonating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!tenantId) return;
    setResetting(true);
    try {
      const res = await api.superAdmin.resetTenantPassword(tenantId);
      // P24: the server no longer sets a known password — Cognito emails the owner a reset code.
      setActionMessage((res as any).message || `Password reset email sent to ${res.email}`);
      setShowModal(null);
    } catch (err: any) {
      setActionMessage(err?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleResendWelcomeEmail = async () => {
    if (!tenantId) return;
    setSendingWelcome(true);
    try {
      const res = await api.superAdmin.resendWelcomeEmail(tenantId);
      setActionMessage((res as any).message || `Welcome email sent to ${res.email}`);
      setShowModal(null);
    } catch (err: any) {
      setActionMessage(err?.message || 'Failed to resend');
    } finally {
      setSendingWelcome(false);
    }
  };

  const handleSendEmail = async () => {
    if (!tenantId || !emailSubject.trim() || !emailBody.trim()) return;
    setEmailSending(true);
    try {
      const toList = emailExtraRecipients.split(',').map(v => v.trim()).filter(Boolean);
      const res = await api.superAdmin.sendTenantEmail(tenantId, {
        subject: emailSubject.trim(),
        message: emailBody.trim(),
        to: toList.length ? toList : undefined
      });
      setEmailStatus(`Email sent to ${res.to.join(', ')}`);
      setEmailBody('');
    } catch (err: any) {
      setEmailStatus(err?.message || 'Failed to send');
    } finally {
      setEmailSending(false);
    }
  };

  const handleSuspendTenant = async () => {
    if (!tenantId) return;
    setSuspending(true);
    try {
      const newStatus = tenant?.status === 'suspended' ? 'active' : 'suspended';
      await api.superAdmin.updateTenantStatus(tenantId, newStatus);
      setActionMessage(newStatus === 'suspended' ? 'Tenant suspended successfully' : 'Tenant unsuspended successfully');
      setShowModal(null);
      await load();
    } catch (err: any) {
      setActionMessage(err?.message || 'Failed to update tenant status');
    } finally {
      setSuspending(false);
    }
  };

  const handlePostSupportComment = async () => {
    if (!selectedIssueId || !supportReplyText.trim()) return;
    setSupportPosting(true);
    try {
      await issuesApi.comment(selectedIssueId, supportReplyText.trim(), { internal: supportInternalNote, kind: 'comment' });
      await loadIssueDetail(selectedIssueId);
      setSupportReplyText('');
      setSupportInternalNote(false);
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to send reply');
    } finally {
      setSupportPosting(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // Computed values
  const currentPlan = prices.find(p => p.id === tenant?.priceId);
  const displayAmount = currentPlan?.unitAmount ?? tenant?.subscriptionCost;

  const statusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'suspended': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'grace': return 'bg-stone-500/20 text-stone-300 border-stone-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Does this tenant have the POS AI Agents package enabled?
  // Drives visibility of the POS AI Agents tab (spec §8.5).
  // Tab label name is sourced from the enabled package's `name` so it
  // stays in sync across product rebrands.
  const posAiPkg = useMemo(() => {
    // Package tiers: match any tier of the family that holds pkg_pos_agents,
    // not just that one id — a tenant on a higher tier still gets the tab.
    const aiFamily = families.find((f) => Array.isArray(f?.tierIds) && f.tierIds.includes('pkg_pos_agents'));
    const aiIds = aiFamily ? aiFamily.tierIds : ['pkg_pos_agents'];
    const enabled = tenantPackages.find((p) => aiIds.includes(p.packageId) && p.status === 'active');
    if (!enabled) return null;
    const def = allPackages.find((pk) => pk.id === 'pkg_pos_agents');
    return { label: def?.name || 'POS AI Agents' };
  }, [tenantPackages, allPackages, families]);

  // Billed vs Enabled panel (Task 5) — id -> display name for the package
  // list, and the last 5 `activate_packages`-style runs off the SAME
  // audit-log rows already loaded into `auditLogs` (no second fetch; runs are
  // written into that table with `kind: 'action_block_run'`).
  // Spec §3.3 — one toggle row per package: a held lower tier shows in its
  // package's slot as "Name · <tier>" and toggles THAT tier id; a family the
  // tenant does not hold shows the top id. No families ⇒ allPackages as-is.
  const packageRows = useMemo(
    () => tenantPackageRows(
      allPackages,
      families,
      tenantPackages.filter((tp) => tp.status === 'active').map((tp) => tp.packageId),
    ),
    [allPackages, families, tenantPackages],
  );

  const packageNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    allPackages.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [allPackages]);

  const actionBlockRuns = useMemo(
    () => auditLogs.filter((e: any) => e?.kind === 'action_block_run').slice(0, 5),
    [auditLogs],
  );

  const handleActionRan = async () => {
    await Promise.all([load(), loadBilledPackages()]);
  };

  // Tab badge = tickets still needing attention (new / in progress / awaiting
  // re-test), matching the "count of things needing action" convention the
  // other tab badges (Packages, Locations) already use rather than a raw total.
  const openSupportCount = useMemo(
    () => supportIssues.filter((i) => SUPPORT_OPEN_STATUSES.has(i.status)).length,
    [supportIssues],
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'finance', label: 'Finance', icon: DollarSign },
    { id: 'packages', label: 'Plan & Packages', icon: Package, count: canManagePackages ? tenantPackages.filter(p => p.status === 'active').length : undefined },
    { id: 'support', label: 'Support', icon: MessageSquare, count: openSupportCount },
    { id: 'locations', label: 'Locations', icon: MapPin, count: locationRequests.length },
    // Everything this customer has ever done, in one list. The two-year view:
    // orders, extra stores, upgrades, claims, collections.
    { id: 'history', label: 'History', icon: History },
    { id: 'admin', label: 'Admin', icon: Shield },
    { id: 'pos', label: 'POS', icon: Monitor, count: undefined },
    { id: 'advertising', label: 'Advertising', icon: Tv, count: undefined },
    { id: 'kiosks', label: 'Kiosks', icon: Tablet, count: undefined },
    ...(posAiPkg
      ? [{ id: 'pos-ai' as const, label: posAiPkg.label, icon: Sparkles, count: undefined }]
      : []),
  ];

  // Skeleton-first: paint the page shape immediately while the tenant loads
  if (loading && !tenant) {
    return <TenantProfileSkeleton />;
  }

  return (
    <div className="min-h-screen bg-black -m-3 md:-m-6">
      {/* Black Header Bar */}
      <header className="bg-black text-white px-4 md:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Back + Tenant Info */}
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <button
              onClick={() => navigate('/command-center')}
              className="p-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 transition-colors shrink-0"
            >
              <ArrowLeft size={18} />
            </button>

            <div className="min-w-0">
              {/* Business Name with Copy */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 group">
                  <h1 className="text-lg md:text-xl font-bold text-white truncate">{tenant?.name || tenant?.businessName || 'Tenant'}</h1>
                  <button
                    onClick={() => copyToClipboard(tenant?.name || tenant?.businessName || '', 'name')}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-opacity"
                    title="Copy business name"
                  >
                    {copiedId === 'name' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  </button>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusColor(tenant?.status)}`}>
                  {tenant?.status || 'Unknown'}
                </span>
              </div>
              {/* Email with Copy */}
              <div className="flex items-center gap-1.5 group">
                <p className="text-white text-xs truncate">{tenant?.ownerEmail}</p>
                <button
                  onClick={() => copyToClipboard(tenant?.ownerEmail || '', 'email')}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-white transition-opacity shrink-0"
                  title="Copy email"
                >
                  {copiedId === 'email' ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                </button>
              </div>
              {/* Tenant ID - Very Small */}
              <div className="flex items-center gap-1 group mt-0.5">
                <p className="text-gray-500 text-[10px] font-mono truncate">ID: {tenant?.id?.slice(0, 12)}...</p>
                <button
                  onClick={() => copyToClipboard(tenant?.id || '', 'tenantId')}
                  className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-white transition-opacity shrink-0"
                  title="Copy tenant ID"
                >
                  {copiedId === 'tenantId' ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                </button>
              </div>
            </div>
          </div>

          {/* Center: Quick Stats (hidden on mobile) */}
          <div className="hidden lg:flex items-center gap-6 px-6 border-x border-white/10">
            <div className="text-center">
              <p className="text-lg font-bold text-white">{formatMoney(displayAmount)}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Monthly</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-white">{formatDate(tenant?.currentPeriodEnd)}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Next Payment</p>
            </div>
            <div className="text-center">
              <p className={`text-lg font-bold ${tenant?.subscriptionStatus === 'active' ? 'text-emerald-400' : 'text-red-400'}`}>
                {tenant?.subscriptionStatus || 'N/A'}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Subscription</p>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleImpersonate}
              disabled={impersonating}
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-gray-800 text-white rounded-xl font-semibold text-sm hover:bg-gray-700 transition-colors"
            >
              <Eye size={16} />
              <span className="hidden sm:inline">{impersonating ? 'Opening...' : 'View as Owner'}</span>
            </button>
            <button
              onClick={() => { setLoading(true); load(); }}
              disabled={loading}
              className="p-2.5 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors"
            >
              {loading ? <RubyGemMark size={18} drawing color="currentColor" /> : <RefreshCw size={18} />}
            </button>
            {/* Every tenant page reaches the Finance Hub; the Hub links back here. */}
            <button
              onClick={() => navigate('/finance')}
              title="Open Finance Hub"
              className="px-3 py-2 rounded-lg border border-white/20 text-white/90 hover:bg-white/10 text-sm min-h-[40px] inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              Finance Hub <span aria-hidden>&rarr;</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mt-4 -mb-4 overflow-x-auto pb-0 scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as MainTab)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-emerald-500/30 text-emerald-300' : 'bg-white/10'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      {/* Gradient Line */}
      <div className="h-1" style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)' }} />

      {/* Toast Message */}
      {actionMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-white text-stone-800 px-4 py-3 rounded-2xl shadow-lg border border-stone-200 flex items-center gap-3 max-w-md">
          <span className="text-sm">{actionMessage}</span>
          <button onClick={() => setActionMessage(null)} className="text-stone-400 hover:text-stone-600">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Content Area */}
      <div className="bg-stone-100 min-h-screen">
        {/* Main Content */}
        <main className="p-4 md:p-6 lg:p-8 w-full max-w-[90%] mx-auto">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-5">
            {/* Quick Actions - Now on top */}
            <CollapsibleSection
              title="Quick Actions"
              icon={<Zap size={16} className="text-stone-600" />}
              expanded={expandedSections.has('actions')}
              onToggle={() => toggleSection('actions')}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <ActionButton icon={<DollarSign size={16} />} label="Finance" onClick={() => setActiveTab('finance')} />
                <ActionButton icon={<KeyRound size={16} />} label="Reset Password" onClick={() => setShowModal('reset')} />
                <ActionButton icon={<Mail size={16} />} label="Resend Welcome" onClick={() => setShowModal('welcome')} />
                <ActionButton
                  icon={tenant?.status === 'suspended' ? <Unlock size={16} /> : <Lock size={16} />}
                  label={tenant?.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
                  onClick={() => setShowModal('suspend')}
                />
              </div>
            </CollapsibleSection>

            {/* Tenant Details */}
            <CollapsibleSection
              title="Tenant Details"
              icon={<Building2 size={16} className="text-stone-500" />}
              expanded={expandedSections.has('details')}
              onToggle={() => toggleSection('details')}
            >
              <div className="grid md:grid-cols-2 gap-3">
                <InfoRow label="Tenant ID" value={tenant?.id} mono />
                <InfoRow label="Owner Email" value={tenant?.ownerEmail} />
                <InfoRow label="Created" value={formatDate(tenant?.createdAt)} />
                <InfoRow label="Source" value={tenant?.source} />
              </div>
            </CollapsibleSection>

          </div>
        )}

        {/* FINANCE TAB — Plan / Locations / Devices / History (spec §9).
            The old `billing` tab and the money half of `overview` fold in here;
            `?tab=billing` still resolves, landing on the History panel. */}
        {activeTab === 'finance' && (
          <TenantFinancePanels
            tenantId={tenantId!}
            tenant={tenant}
            locations={locationRequests}
            locationsLoading={locationsLoading}
            billingEvents={billingEvents}
            prices={prices}
            pricesLoading={pricesLoading}
            panel={financePanel}
            onPanelChange={changeFinancePanel}
            onReload={load}
            onReloadLocations={fetchLocationRequests}
            notify={setActionMessage}
          />
        )}

        {/* PACKAGES TAB */}
        {activeTab === 'packages' && (
          <div className="space-y-5">
            {/* Tenant Product Info */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                  <Box size={20} className="text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800">Product & Deal Information</h3>
                  <p className="text-sm text-stone-500">How this tenant was created</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                  <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Product Type</p>
                  {/* READ-ONLY. The assigned product is decided by the deal the
                      tenant signed on (deals-service stamps `productId` onto
                      the tenant as `productType` at signup) — an operator
                      re-pointing it here silently divorced the tenant from its
                      own deal and re-filtered its packages against a product it
                      never bought. Shown as a label so the two panels in this
                      row always agree. */}
                  <p className="text-lg font-semibold text-stone-800">
                    {tenant?.productType
                      ? (availableProducts.find(p => p.id === tenant.productType)?.name || tenant.productType)
                      : 'No Product'}
                  </p>
                  {!tenant?.productType && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      Tenant has no product assigned
                    </p>
                  )}
                  {/* The deal is the source of truth. When the tenant's stored
                      productType disagrees with it, say so rather than quietly
                      showing the wrong product — the packages list below is
                      filtered by this value. */}
                  {tenantDeal?.productId && tenant?.productType && tenantDeal.productId !== tenant.productType && (
                    <p className="text-xs text-amber-600 mt-2 flex items-start gap-1">
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      <span>
                        Deal {tenant.dealCode} is for{' '}
                        <span className="font-semibold">
                          {availableProducts.find(p => p.id === tenantDeal.productId)?.name || tenantDeal.productId}
                        </span>
                        {' '}— this tenant predates the fix that stamps the deal's product at signup.
                      </span>
                    </p>
                  )}
                </div>
                <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                  <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Deal</p>
                  {tenant?.dealCode ? (
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-900 text-white rounded-lg text-sm font-mono font-semibold">
                          <Tag size={13} className="text-rose-400" />
                          {tenant.dealCode}
                        </span>
                        {tenantDeal && (
                          <span className="text-sm font-medium text-stone-700">{tenantDeal.name}</span>
                        )}
                        {tenantDeal?.priceCents !== undefined && (
                          <span className="text-xs text-stone-500 tabular-nums">
                            {formatMoney(tenantDeal.priceCents)}/mo
                          </span>
                        )}
                      </div>
                      {tenantDeal ? (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-stone-500">
                            {tenantDeal.packageIds?.length || 0} package{(tenantDeal.packageIds?.length || 0) === 1 ? '' : 's'} included
                            {Object.keys(tenantDeal.packageDiscounts || {}).length > 0 &&
                              ` · ${Object.keys(tenantDeal.packageDiscounts || {}).length} with deal discounts`}
                          </p>
                          {Number.isInteger(tenantDeal.freeLocationQuota) && Number.isInteger(tenantDeal.additionalLocationPriceCents) ? (
                            <p className="text-xs text-stone-500">
                              {tenantDeal.freeLocationQuota} free location{tenantDeal.freeLocationQuota === 1 ? '' : 's'} ·
                              extra location {formatMoney(tenantDeal.additionalLocationPriceCents)}/mo
                            </p>
                          ) : (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                              <AlertTriangle size={11} />
                              No location terms on this deal yet — set them on next deal edit
                            </p>
                          )}
                          <div className="flex gap-2 pt-1">
                            <button
                              onClick={handleReapplyDealPackages}
                              disabled={reapplyingDeal}
                              className="text-xs px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                            >
                              {reapplyingDeal && <RubyGemMark size={11} drawing color="currentColor" />}
                              Re-apply deal packages
                            </button>
                            <button
                              onClick={() => navigate(`/deals/${tenantDeal.id}`)}
                              className="text-xs px-2.5 py-1 border border-stone-300 text-stone-600 hover:bg-stone-100 rounded-lg font-medium"
                            >
                              Edit deal
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-stone-400 mt-2">Deal details unavailable</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-lg font-semibold text-stone-400">Direct Signup</p>
                  )}
                </div>
              </div>
            </div>

            {/* Billed vs Enabled — the missing link between what Finance
                committed (the active PlanAssignment) and what the software
                actually turned on (tenant.enabledPackages). Task 5. */}
            {tenantId && (
              <BilledVsEnabledPanel
                tenantId={tenantId}
                enabledPackages={tenant?.enabledPackages || []}
                billedPackages={billedPackageIds}
                billedLoading={billedPackagesLoading}
                billedError={billedPackagesError}
                packageNames={packageNamesById}
                lastRuns={actionBlockRuns}
                onRan={handleActionRan}
              />
            )}

            {/* Package Management with Toggle Switches */}
            <div key={`packages-${tenant?.productType || 'none'}`} className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
              <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package size={18} className="text-stone-600" />
                  <h3 className="font-semibold text-stone-800">Package Management</h3>
                  {canManagePackages && allPackages.length > 0 && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                      {tenantPackages.filter(p => p.status === 'active').length} / {packageRows.length} enabled
                    </span>
                  )}
                </div>
                <button
                  onClick={loadTenantPackages}
                  disabled={packagesLoading}
                  className="p-2 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100 transition-colors"
                >
                  {packagesLoading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw size={16} />}
                </button>
              </div>

              {packagesLoading ? (
                <RubyLoader variant="section" label="Loading packages..." />
              ) : !canManagePackages ? (
                <div className="p-10 text-center">
                  <Package size={40} className="mx-auto mb-3 text-stone-300" />
                  <p className="text-stone-500">Assign a product first to manage packages</p>
                  <p className="text-xs text-stone-400 mt-1">Use the Product Type selector above</p>
                </div>
              ) : allPackages.length === 0 ? (
                <div className="p-10 text-center">
                  <Package size={40} className="mx-auto mb-3 text-stone-300" />
                  <p className="text-stone-500">No packages available for this product</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {packageRows.map(pkg => {
                    const assignment = tenantPackages.find(tp => tp.packageId === pkg.id && tp.status === 'active');
                    const isEnabled = !!assignment;
                    const isToggling = togglingPackage === pkg.id;

                    // Deal attribution (2026-08-26): a package the deal
                    // includes carries the deal's discount; one enabled
                    // OUTSIDE the deal bills at list price — said before
                    // the operator commits, not discovered on the invoice.
                    // Same rounding as shared-types applyDiscount (round
                    // once, floor at zero); display only — billing composes
                    // server-side.
                    const inDeal = !!tenantDeal?.packageIds?.includes(pkg.id);
                    const dealDiscount = inDeal ? tenantDeal?.packageDiscounts?.[pkg.id] : undefined;
                    const listCents = pkg.basePrice || 0;
                    const netCents = dealDiscount
                      ? (dealDiscount.percentOff !== undefined
                          ? Math.max(0, Math.round(listCents * (1 - dealDiscount.percentOff / 100)))
                          : Math.max(0, listCents - (dealDiscount.amountOffCents || 0)))
                      : listCents;

                    return (
                      <div key={pkg.id} className="px-5 py-4 flex items-center justify-between hover:bg-stone-50/50 transition-colors">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {(() => {
                            const Icon = getIcon(pkg.icon || '');
                            const iconBgStyle = isEnabled
                              ? pkg.colorType === 'gradient'
                                ? { background: `linear-gradient(135deg, ${pkg.gradientFrom || '#e8365a'}, ${pkg.gradientTo || '#ec4899'})` }
                                : { backgroundColor: pkg.color || '#64748b' }
                              : { backgroundColor: '#e2e8f0' };
                            return (
                              <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                                style={iconBgStyle}
                              >
                                <Icon size={18} className={isEnabled ? 'text-white' : 'text-stone-400'} />
                              </div>
                            );
                          })()}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`font-medium ${isEnabled ? 'text-stone-800' : 'text-stone-500'}`}>
                                {pkg.name}
                              </p>
                              {pkg.isCore && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">Core</span>
                              )}
                              {inDeal && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded font-medium">In deal</span>
                              )}
                              {isEnabled && !inDeal && !pkg.isCore && listCents > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-medium border border-amber-200">Bills at list</span>
                              )}
                            </div>
                            <p className="text-sm text-stone-500 line-clamp-1">{pkg.description}</p>
                            <p className="text-xs text-stone-500 mt-0.5">
                              {listCents === 0 ? 'Free' : dealDiscount ? (
                                <>
                                  <span className="line-through text-stone-400">${(listCents / 100).toFixed(2)}</span>
                                  {' '}
                                  <span className="font-semibold text-stone-700">${(netCents / 100).toFixed(2)}/mo</span>
                                  {' '}
                                  <span className="text-emerald-600 font-medium">
                                    ({dealDiscount.percentOff !== undefined
                                      ? `−${dealDiscount.percentOff}%`
                                      : `−$${((dealDiscount.amountOffCents || 0) / 100).toFixed(2)}`} deal{dealDiscount.reason ? ` · ${dealDiscount.reason}` : ''})
                                  </span>
                                </>
                              ) : `$${(listCents / 100).toFixed(2)}/mo`}
                            </p>
                            {isEnabled && assignment && (
                              <p className="text-xs text-stone-400 mt-0.5">
                                Enabled {formatDate(assignment.assignedAt)}
                              </p>
                            )}
                            {pkg.features && pkg.features.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {pkg.features.slice(0, 4).map((f, i) => (
                                  <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    isEnabled ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-500'
                                  }`}>
                                    {f}
                                  </span>
                                ))}
                                {pkg.features.length > 4 && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-400 rounded">
                                    +{pkg.features.length - 4}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Toggle Switch */}
                        <div className="ml-4 shrink-0">
                          {isToggling ? (
                            <RubyGemMark size={20} drawing />
                          ) : (
                            <button
                              onClick={() => setPackageToggleConfirm({
                                packageId: pkg.id,
                                packageName: pkg.name,
                                action: isEnabled ? 'disable' : 'enable'
                              })}
                              className={`relative w-12 h-7 rounded-full transition-colors ${
                                isEnabled ? 'bg-emerald-500' : 'bg-stone-300'
                              }`}
                            >
                              <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                                isEnabled ? 'left-6' : 'left-1'
                              }`} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Software totals — list, deal discounts, net (2026-08-26).
                  Display-only mirror of the deal's per-package discounts;
                  the payable bill is composed server-side on sync/activation. */}
              {(() => {
                const enabledPkgs = allPackages.filter(pkg =>
                  tenantPackages.some(tp => tp.packageId === pkg.id && tp.status === 'active'));
                let listTotal = 0; let discountTotal = 0;
                for (const pkg of enabledPkgs) {
                  const listCents = pkg.basePrice || 0;
                  listTotal += listCents;
                  const d = tenantDeal?.packageIds?.includes(pkg.id) ? tenantDeal?.packageDiscounts?.[pkg.id] : undefined;
                  if (d) {
                    const net = d.percentOff !== undefined
                      ? Math.max(0, Math.round(listCents * (1 - d.percentOff / 100)))
                      : Math.max(0, listCents - (d.amountOffCents || 0));
                    discountTotal += listCents - net;
                  }
                }
                const netTotal = listTotal - discountTotal;
                return (
                  <div className="px-5 py-4 border-t border-stone-200 bg-stone-50 space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-500">Software list</span>
                      <span className="tabular-nums text-stone-600">${(listTotal / 100).toFixed(2)}</span>
                    </div>
                    {discountTotal > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-emerald-600">Deal discounts</span>
                        <span className="tabular-nums text-emerald-600">−${(discountTotal / 100).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1 border-t border-dashed border-stone-200">
                      <span className="text-sm font-medium text-stone-700">Software / month</span>
                      <span className="text-lg font-bold text-stone-900 tabular-nums">${(netTotal / 100).toFixed(2)}</span>
                    </div>
                    <p className="text-[11px] text-stone-400 pt-0.5">
                      Locations billed separately below. The payable amount is composed by billing on sync.
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Locations & Billing — REBUILT 2026-09-07.

                It used to be fed by `location-requests` alone: the money model
                that PRECEDED per-location billing, where a location beyond the
                free quota was approved at a price and `isFree` on that row was
                the answer. A migrated tenant has none of that — every store
                carries its own subscription — so the card showed "Free" against
                stores Stripe was billing, and omitted entirely a store that had
                a subscription but no request row.

                Rows now come from the billing side, priced by each
                subscription's UPCOMING INVOICE. The three old money actions are
                gone: see LocationsBillingPanel's own header. */}
            <LocationsBillingPanel
              driftLocations={driftLocations}
              driftLoading={driftLocationsLoading}
              driftError={driftLocationsError}
              requests={locationRequests}
              requestsLoading={locationsLoading}
              freeQuota={tenant?.freeLocationQuota ?? 1}
              dealFreeQuota={tenantDeal?.freeLocationQuota}
              additionalLocationPriceCents={tenantDeal?.additionalLocationPriceCents}
              onRefresh={() => { fetchLocationRequests(); loadDriftLocations(); }}
              onOpen={(row) => navigate(`/tenant/${tenantId}/location/${row.requestId}`)}
              onReviewDocs={(row) => navigate(`/tenant/${tenantId}/location/${row.requestId}`)}
              onApprove={(row) => {
                const req = locationRequests.find((r: any) => r.requestId === row.requestId);
                if (req) setLocationApproveModal(req);
              }}
            />

            {/* Approve-location modal (default price + comp/discount) */}
            {locationApproveModal && tenantId && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setLocationApproveModal(null)}>
                <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-stone-800 mb-1">Approve Location</h3>
                  <p className="text-sm text-stone-500 mb-4">{locationApproveModal.name}</p>
                  <ApproveLocationForm
                    tenantId={tenantId}
                    requestId={locationApproveModal.requestId}
                    locationName={locationApproveModal.name}
                    dealHint={tenantDeal && Number.isInteger(tenantDeal.additionalLocationPriceCents)
                      ? { additionalLocationPriceCents: tenantDeal.additionalLocationPriceCents, dealCode: tenantDeal.code }
                      : null}
                    onDone={(message) => {
                      toastService.success(message);
                      setLocationApproveModal(null);
                      fetchLocationRequests();
                    }}
                    onCancel={() => setLocationApproveModal(null)}
                  />
                </div>
              </div>
            )}

            {/*
              THE MANAGE-BILLING MODAL WAS DELETED 2026-09-07.

              It hosted `AdminBillingTab` — Make free, Change price, Deactivate —
              all of which act on `location-requests.stripeSubscriptionId`, the
              per-location subscription of the model that preceded per-location
              billing. That field is null on every migrated store, so "Make free"
              reported a cancellation that never happened while Stripe kept
              charging, and "Deactivate" could not cancel either but DID set the
              location inactive, taking a live store offline mid-subscription.

              Removed rather than disabled: what a store pays is changed through
              Plan assignment on the Finance tab. The location-billing endpoints
              stay live for tenants still on the old model.
            */}

            {/* Package Toggle Confirmation Modal */}
            {packageToggleConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
                  <div className="p-6">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      packageToggleConfirm.action === 'enable' ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      {packageToggleConfirm.action === 'enable' ? (
                        <Check className="text-emerald-600" size={24} />
                      ) : (
                        <AlertTriangle className="text-red-600" size={24} />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-stone-800 text-center mb-2">
                      {packageToggleConfirm.action === 'enable' ? 'Enable' : 'Disable'} Package?
                    </h3>
                    <p className="text-stone-600 text-center mb-1">
                      <span className="font-medium">{packageToggleConfirm.packageName}</span>
                    </p>
                    <p className={`text-sm text-center mb-6 ${
                      packageToggleConfirm.action === 'enable' ? 'text-stone-500' : 'text-red-600'
                    }`}>
                      {packageToggleConfirm.action === 'enable'
                        ? 'This will grant the tenant access to all features in this package.'
                        : 'Warning: This will immediately block access to all features in this package for the tenant.'}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setPackageToggleConfirm(null)}
                        className="flex-1 px-4 py-2.5 border border-stone-300 text-stone-700 rounded-xl hover:bg-stone-50 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleTogglePackage}
                        disabled={!!togglingPackage}
                        className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 ${
                          packageToggleConfirm.action === 'enable'
                            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                            : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                      >
                        {togglingPackage ? (
                          <span className="flex justify-center"><RubyGemMark size={18} drawing color="currentColor" /></span>
                        ) : (
                          packageToggleConfirm.action === 'enable' ? 'Enable Package' : 'Disable Package'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* SUPPORT TAB — unified support-service issues (2026-08-26). The old
            HQ-messaging system (api.superAdmin.listTickets/createTicket/...)
            is gone; admin-initiated outreach to this tenant still lives in
            the Admin tab's Send Email panel, since the unified backend has
            no admin-create-ticket endpoint yet. */}
        {activeTab === 'support' && (
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
            <div className="grid lg:grid-cols-[320px_1fr] h-[600px]">
              {/* Ticket List - Left Panel */}
              <div className="border-r border-stone-200 flex flex-col">
                {/* Header */}
                <div className="px-4 py-4 border-b border-stone-100 bg-stone-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Headphones size={18} className="text-stone-600" />
                      <h3 className="font-semibold text-stone-800">Support Tickets</h3>
                    </div>
                    <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{supportIssues.length}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/support')}
                    className="w-full text-center text-xs font-semibold text-emerald-700 hover:text-emerald-800 py-1"
                  >
                    Open in Support Center →
                  </button>
                </div>

                {/* Ticket List */}
                <div className="flex-1 overflow-y-auto">
                  {issueDetailLoading && supportIssues.length === 0 ? (
                    <RubyLoader variant="section" className="h-full" />
                  ) : supportIssues.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-stone-400 p-6">
                      <MessageSquare size={40} className="mb-3 opacity-30" />
                      <p className="text-sm">No support tickets yet</p>
                      <p className="text-xs mt-1">Tickets this tenant raises will show up here</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stone-100">
                      {supportIssues.map(issue => {
                        const meta = SUPPORT_STATUS_META[issue.status] || { label: issue.status, cls: 'bg-stone-100 text-stone-600' };
                        return (
                          <button
                            key={issue.id}
                            type="button"
                            onClick={() => setSelectedIssueId(issue.id)}
                            className={`w-full text-left p-4 transition-colors ${selectedIssueId === issue.id ? 'bg-emerald-50 border-l-3 border-emerald-500' : 'hover:bg-stone-50'}`}
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-sm flex-none mt-0.5">{SUPPORT_KIND_EMOJI[issue.kind || 'issue'] || '✉️'}</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-stone-800 font-medium truncate text-sm">{issue.title || 'No subject'}</p>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${meta.cls}`}>{meta.label}</span>
                                  <span className="text-stone-400 text-xs">{supportTimeAgo(issue.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Detail Panel - Right Side */}
              <div className="flex flex-col bg-stone-50/30">
                {!selectedIssueId ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-stone-400">
                    <Headphones size={40} className="mb-3 opacity-30" />
                    <p className="text-sm">Select a ticket to view the conversation</p>
                  </div>
                ) : issueDetailLoading && (!issueDetail || issueDetail.issue.id !== selectedIssueId) ? (
                  <RubyLoader variant="section" className="flex-1" />
                ) : !issueDetail ? (
                  <div className="flex-1 flex items-center justify-center text-stone-400 p-6 text-center text-sm">
                    Failed to load this ticket.
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-stone-100 bg-white">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-none">
                          <Headphones size={18} className="text-emerald-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-stone-800 truncate">{issueDetail.issue.title}</h3>
                          <p className="text-xs text-stone-400">
                            {SUPPORT_KIND_LABEL[issueDetail.issue.kind || 'issue'] || 'Ticket'} · Opened {formatDateTime(issueDetail.issue.createdAt)}
                          </p>
                        </div>
                        {(() => {
                          const meta = SUPPORT_STATUS_META[issueDetail.issue.status] || { label: issueDetail.issue.status, cls: 'bg-stone-100 text-stone-600' };
                          return <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold whitespace-nowrap flex-none ${meta.cls}`}>{meta.label}</span>;
                        })()}
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-3">
                      {buildSupportTimeline(issueDetail.issue, issueDetail.comments).map((e, i) => {
                        if (e.kind === 'created') {
                          return (
                            <div key={i} className="flex items-start gap-2 text-xs text-stone-400">
                              <span className="w-6 h-6 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-[11px] shrink-0">+</span>
                              <div>
                                <span className="text-stone-600 font-semibold">Ticket created</span> — {SUPPORT_KIND_LABEL[issueDetail.issue.kind || 'issue'] || 'Ticket'} request
                                <div className="text-[10px] text-stone-400 mt-0.5">{formatDateTime(e.ts)}</div>
                              </div>
                            </div>
                          );
                        }
                        if (e.kind === 'status') {
                          return (
                            <div key={i} className="flex items-start gap-2 text-xs text-stone-400">
                              <span className="w-6 h-6 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-[11px] shrink-0">⇄</span>
                              <div>
                                <span className="text-stone-600">{e.text}</span>
                                <div className="text-[10px] text-stone-400 mt-0.5">{formatDateTime(e.ts)}</div>
                              </div>
                            </div>
                          );
                        }
                        if (e.kind === 'call') {
                          return (
                            <div key={i} className="bg-amber-50 border border-dashed border-amber-400 rounded-lg px-3 py-2 text-xs text-amber-800">
                              📞 <span className="font-bold text-amber-900">Phone call</span> — {e.comment.body}
                              <div className="text-[10px] text-amber-600 mt-1">
                                Logged by {e.comment.authorName || e.comment.authorEmail} · {formatDateTime(e.ts)}
                              </div>
                            </div>
                          );
                        }
                        const isRight = e.side === 'right';
                        return (
                          <div
                            key={i}
                            className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${isRight ? 'ml-auto bg-emerald-500 text-white rounded-br-md' : 'bg-white border border-stone-200 text-stone-800 rounded-bl-md shadow-sm'}`}
                          >
                            <div className={`text-[10.5px] font-bold mb-1 flex items-center ${isRight ? 'text-emerald-50' : 'text-stone-500'}`}>
                              {e.comment.authorName || e.comment.authorEmail || (isRight ? 'Support' : (tenant?.businessName || 'Tenant'))}
                              {e.comment.internal && (
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold normal-case align-middle ${isRight ? 'bg-black/15 text-white' : 'bg-stone-200 text-stone-500'}`}>internal</span>
                              )}
                            </div>
                            <p className="whitespace-pre-wrap">{e.comment.body}</p>
                            <div className={`text-[10px] mt-1 ${isRight ? 'text-emerald-50/70' : 'text-stone-400'}`}>{formatDateTime(e.ts)}</div>
                          </div>
                        );
                      })}
                      {issueDetail.comments.length === 0 && (
                        <p className="text-sm text-stone-400 text-center py-6">No replies yet.</p>
                      )}
                      <div ref={supportMessagesEndRef} />
                    </div>

                    {/* Composer */}
                    <div className="p-4 border-t border-stone-200 bg-white">
                      <textarea
                        value={supportReplyText}
                        onChange={e => setSupportReplyText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handlePostSupportComment(); }}
                        rows={2}
                        placeholder="Type your reply… (Cmd+Enter to send)"
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 resize-none text-sm"
                      />
                      <div className="flex items-center justify-between mt-2.5">
                        <label className="flex items-center gap-1.5 text-xs text-stone-500 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={supportInternalNote}
                            onChange={e => setSupportInternalNote(e.target.checked)}
                            className="rounded border-stone-300"
                          />
                          Internal note
                        </label>
                        <button
                          type="button"
                          onClick={handlePostSupportComment}
                          disabled={supportPosting || !supportReplyText.trim()}
                          className="px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
                        >
                          {supportPosting ? (
                            <RubyGemMark size={16} drawing color="currentColor" />
                          ) : (
                            <Send size={16} />
                          )}
                          Send
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ADMIN TAB */}
        {activeTab === 'history' && tenantId && (
          <div className="space-y-4">
            {/*
              APPEND-ONLY, and it outlives every order on it. This is what a
              warranty claim, a chargeback or a collection is argued from years
              after the fact — which is why nothing here can be edited.
            */}
            <JourneyTimeline
              tenantId={tenantId}
              title="Everything that has happened for this customer"
              limit={200}
            />
          </div>
        )}

        {activeTab === 'admin' && (
          <div className="space-y-5">
            {/* Send Email */}
            <CollapsibleSection
              title="Send Email"
              icon={<Mail size={16} className="text-stone-600" />}
              expanded={expandedSections.has('email')}
              onToggle={() => toggleSection('email')}
            >
              <div className="space-y-3">
                <input
                  type="text"
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  placeholder="Subject"
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400/50"
                />
                <textarea
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  placeholder="Message..."
                  rows={4}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400/50 resize-none"
                />
                <input
                  type="text"
                  value={emailExtraRecipients}
                  onChange={e => setEmailExtraRecipients(e.target.value)}
                  placeholder="CC: extra@email.com, another@email.com"
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-400/50"
                />
                <div className="flex items-center justify-between">
                  {emailStatus && <p className="text-emerald-600 text-sm">{emailStatus}</p>}
                  <button onClick={handleSendEmail} disabled={emailSending || !emailSubject.trim() || !emailBody.trim()} className="px-5 py-2.5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 disabled:opacity-50 ml-auto">
                    {emailSending ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </CollapsibleSection>

            {/* Admin Activity */}
            <CollapsibleSection
              title="Admin Activity"
              icon={<UserCog size={16} className="text-rose-500" />}
              expanded={expandedSections.has('adminActivity')}
              onToggle={() => toggleSection('adminActivity')}
              badge={adminActivity.length}
            >
              {adminActivity.length === 0 ? (
                <div className="text-center text-stone-400 py-4">No admin activity recorded</div>
              ) : (
                <div className="divide-y divide-stone-100 max-h-[300px] overflow-y-auto -mx-4">
                  {adminActivity.map((ev, i) => (
                    <div key={i} className="px-4 py-3 hover:bg-stone-50">
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          ev.type === 'impersonation' ? 'bg-rose-100 text-rose-700' :
                          ev.type === 'password_reset' ? 'bg-stone-200 text-stone-700' :
                          'bg-stone-100 text-stone-600'
                        }`}>
                          {ev.type?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-stone-400 text-xs">{formatDateTime(ev.requestedAt || ev.createdAt)}</span>
                      </div>
                      {ev.reason && <p className="text-stone-500 text-xs mt-1">Reason: {ev.reason}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            {/* Audit Log */}
            <CollapsibleSection
              title="Audit Log"
              icon={<FileText size={16} className="text-stone-500" />}
              expanded={expandedSections.has('audit')}
              onToggle={() => toggleSection('audit')}
              badge={auditLogs.length}
            >
              {auditLogs.length === 0 ? (
                <div className="text-center text-stone-400 py-4">No audit events</div>
              ) : (
                <div className="divide-y divide-stone-100 max-h-[300px] overflow-y-auto -mx-4">
                  {auditLogs.map((ev, i) => (
                    <div key={i} className="px-4 py-3 hover:bg-stone-50">
                      <div className="flex items-center justify-between">
                        <span className="text-stone-800 text-sm">{ev.type?.replace(/_/g, ' ')}</span>
                        <span className="text-stone-400 text-xs">{formatDateTime(ev.eventTimestamp)}</span>
                      </div>
                      {ev.details && <p className="text-stone-500 text-xs mt-1">{JSON.stringify(ev.details)}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </div>
        )}

        {/* LOCATIONS TAB */}
        {activeTab === 'locations' && (
          <div className="space-y-4">
            {/* Free location quota lives on Finance -> Plan (spec §9): it decides
                which locations are billed, so it sits with the rest of the money. */}
            <button
              onClick={() => setActiveTab('finance')}
              className="w-full text-left bg-white text-stone-800 rounded-2xl shadow-sm border border-stone-200/60 p-4 hover:border-stone-300"
            >
              <p className="text-stone-800 font-semibold text-sm">Free location quota &amp; per-location billing</p>
              <p className="text-stone-500 text-xs mt-1">
                On the Finance tab — Plan sets the quota, Locations shows one subscription per row.
              </p>
            </button>

            {/* Location Requests List */}
            {locationsLoading ? (
              <RubyLoader variant="section" />
            ) : locationRequests.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <MapPin size={32} className="mx-auto mb-2 opacity-50" />
                <p>No location requests for this tenant</p>
              </div>
            ) : (
              <div className="space-y-3">
                {locationRequests.map((req: any) => (
                  <div
                    key={req.requestId}
                    onClick={() => navigate(`/tenant/${tenantId}/location/${req.requestId}`)}
                    className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-4 transition-colors cursor-pointer hover:border-stone-300 hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center shrink-0">
                        <MapPin size={18} className="text-stone-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-800">{req.name}</p>
                        <p className="text-xs text-stone-500">
                          {req.address?.city}{req.address?.state ? `, ${req.address.state}` : ''} · {req.type || 'store'}
                        </p>
                      </div>
                      {/* Status badge */}
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                        req.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                        req.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                        req.status === 'payment_pending' ? 'bg-orange-100 text-orange-800' :
                        req.status === 'deactivation_requested' ? 'bg-orange-100 text-orange-800' :
                        req.status === 'grace' || req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        {req.status === 'payment_pending' ? 'Payment Pending' :
                         req.status === 'deactivation_requested' ? 'Deactivation Pending' :
                         req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                      </span>
                      <ChevronRight size={16} className="text-stone-400 shrink-0" />
                    </div>
                    {/* Info requested banner */}
                    {req.infoRequested && req.status === 'pending' && (
                      <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-700 font-medium">Info requested from tenant</p>
                        {req.tenantResponse && (
                          <p className="text-xs text-blue-600 mt-1">Response: {req.tenantResponse}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Location Action Modal */}
            {locationActionModal && (
              <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setLocationActionModal(null)}>
                <div
                  className={`${locationActionModal.type === 'approve' ? 'bg-white' : 'bg-gray-900 border border-gray-700'} rounded-2xl p-6 max-w-md w-full`}
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className={`${locationActionModal.type === 'approve' ? 'text-stone-900' : 'text-white'} font-semibold text-lg mb-4`}>
                    {locationActionModal.type === 'approve' && 'Approve Location Request'}
                    {locationActionModal.type === 'reject' && 'Reject Location Request'}
                    {locationActionModal.type === 'info' && 'Request More Information'}
                    {locationActionModal.type === 'comp' && 'Comp Location (Free)'}
                    {locationActionModal.type === 'deactivate' && 'Deactivate Location'}
                    {locationActionModal.type === 'reactivate' && 'Reactivate Location'}
                  </h3>
                  <p className={`${locationActionModal.type === 'approve' ? 'text-stone-500' : 'text-gray-400'} text-sm mb-4`}>
                    Location: <span className={locationActionModal.type === 'approve' ? 'text-stone-800 font-medium' : 'text-white'}>{locationActionModal.request.name}</span>
                  </p>

                  {/* Approve (2026-08-26): default deal/catalog price + comp/discount — same shared form as everywhere else. */}
                  {locationActionModal.type === 'approve' && tenantId && (
                    <ApproveLocationForm
                      tenantId={tenantId}
                      requestId={locationActionModal.request.requestId}
                      locationName={locationActionModal.request.name}
                      dealHint={tenantDeal && Number.isInteger(tenantDeal.additionalLocationPriceCents)
                        ? { additionalLocationPriceCents: tenantDeal.additionalLocationPriceCents, dealCode: tenantDeal.code }
                        : null}
                      onDone={(message) => {
                        toastService.success(message);
                        setLocationActionModal(null);
                        fetchLocationRequests();
                      }}
                      onCancel={() => setLocationActionModal(null)}
                    />
                  )}

                  {(locationActionModal.type === 'reject' || locationActionModal.type === 'info') && (
                    <div className="mb-4">
                      <label className="text-gray-400 text-xs mb-1 block">
                        {locationActionModal.type === 'reject' ? 'Reason for rejection' : 'What information do you need?'}
                      </label>
                      <textarea
                        value={locationModalInput}
                        onChange={(e) => setLocationModalInput(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm h-24 resize-none"
                        placeholder={locationActionModal.type === 'reject' ? 'Enter reason...' : 'Enter your question...'}
                      />
                    </div>
                  )}

                  {locationActionModal.type !== 'approve' && (
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setLocationActionModal(null); setLocationModalInput(''); }} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm">Cancel</button>
                    <button
                      onClick={async () => {
                        const req = locationActionModal.request;
                        try {
                          switch (locationActionModal.type) {
                            case 'reject':
                              await api.locationBilling.rejectRequest(req.requestId, { tenantId: req.tenantId, reason: locationModalInput });
                              break;
                            case 'info':
                              await api.locationBilling.requestInfo(req.requestId, { tenantId: req.tenantId, message: locationModalInput });
                              break;
                            case 'comp':
                              await api.locationBilling.compRequest(req.requestId, { tenantId: req.tenantId });
                              break;
                            case 'deactivate':
                              await api.locationBilling.deactivateRequest(req.requestId, { tenantId: req.tenantId });
                              break;
                            case 'reactivate':
                              await api.locationBilling.reactivateRequest(req.requestId, { tenantId: req.tenantId });
                              break;
                          }
                          setActionMessage(`Location ${locationActionModal.type}d successfully`);
                          setLocationActionModal(null);
                          setLocationModalInput('');
                          fetchLocationRequests();
                        } catch (e: any) {
                          setActionMessage('Action failed: ' + e.message);
                        }
                      }}
                      className={`px-4 py-2 text-white rounded-lg text-sm font-medium ${
                        locationActionModal.type === 'reject' || locationActionModal.type === 'deactivate'
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      {locationActionModal.type === 'reject' ? 'Reject' :
                       locationActionModal.type === 'info' ? 'Send' :
                       locationActionModal.type === 'comp' ? 'Comp (Free)' :
                       locationActionModal.type === 'deactivate' ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pos' && tenant && (
          <PosTerminalTab tenantId={tenant.id} tenantName={tenant.businessName || tenant.name || ''} />
        )}

        {activeTab === 'advertising' && tenant && (
          <AdvertisingTab tenantId={tenant.id} tenantName={tenant.businessName || tenant.name || ''} />
        )}

        {activeTab === 'kiosks' && tenant && (
          <KiosksTab tenantId={tenant.id} tenantName={tenant.businessName || tenant.name || ''} />
        )}

        {activeTab === 'pos-ai' && tenant && posAiPkg && (
          <TenantPosAiAgentsTab
            tenantId={tenant.id}
            tenantName={tenant.businessName || tenant.name || ''}
            active={activeTab === 'pos-ai'}
          />
        )}
        </main>
      </div>

      {/* Modals */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(null)}>
          <div className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            {showModal === 'reset' && (
              <>
                <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <KeyRound className="text-stone-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-stone-800 text-center mb-2">Reset Password?</h3>
                <p className="text-stone-500 text-center text-sm mb-6">
                  This will email {tenant?.ownerEmail} a password reset code (no password is set or shown here)
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowModal(null)} className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200">
                    Cancel
                  </button>
                  <button onClick={handleResetPassword} disabled={resetting} className="flex-1 px-4 py-3 bg-stone-900 text-white font-bold rounded-xl hover:bg-black disabled:opacity-50">
                    {resetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </>
            )}
            {showModal === 'welcome' && (
              <>
                <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Mail className="text-stone-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-stone-800 text-center mb-2">Resend Welcome Email?</h3>
                <p className="text-stone-500 text-center text-sm mb-6">
                  This will send a welcome email with login credentials to {tenant?.ownerEmail}
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowModal(null)} className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200">
                    Cancel
                  </button>
                  <button onClick={handleResendWelcomeEmail} disabled={sendingWelcome} className="flex-1 px-4 py-3 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 disabled:opacity-50">
                    {sendingWelcome ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </>
            )}
            {showModal === 'suspend' && (
              <>
                <div className={`w-12 h-12 ${tenant?.status === 'suspended' ? 'bg-emerald-100' : 'bg-red-100'} rounded-xl flex items-center justify-center mb-4 mx-auto`}>
                  {tenant?.status === 'suspended' ? <Unlock className="text-emerald-600" size={24} /> : <Lock className="text-red-600" size={24} />}
                </div>
                <h3 className="text-xl font-bold text-stone-800 text-center mb-2">
                  {tenant?.status === 'suspended' ? 'Unsuspend Tenant?' : 'Suspend Tenant?'}
                </h3>
                <p className="text-stone-500 text-center text-sm mb-6">
                  {tenant?.status === 'suspended'
                    ? 'This will restore full access to all features for this tenant.'
                    : 'This will restrict the tenant to only Subscription and Settings pages. They will see a suspended message with admin contact option.'}
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowModal(null)} className="flex-1 px-4 py-3 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200">
                    Cancel
                  </button>
                  <button
                    onClick={handleSuspendTenant}
                    disabled={suspending}
                    className={`flex-1 px-4 py-3 ${tenant?.status === 'suspended' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'} text-white font-bold rounded-xl disabled:opacity-50`}
                  >
                    {suspending ? 'Processing...' : tenant?.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

// Reusable Components - Light theme with better readability
const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  badge?: number;
  children: React.ReactNode;
}> = ({ title, icon, expanded, onToggle, badge, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60">
    <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-stone-50 transition-colors">
      <div className="flex items-center gap-3">
        {icon}
        <span className="font-semibold text-stone-800 text-base">{title}</span>
        {badge !== undefined && badge > 0 && (
          <span className="px-2 py-0.5 bg-stone-100 text-stone-500 text-xs rounded-full">{badge}</span>
        )}
      </div>
      <ChevronDown size={18} className={`text-stone-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
    </button>
    {expanded && <div className="px-5 pb-5 pt-1">{children}</div>}
  </div>
);

const ActionButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ icon, label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="flex flex-col items-center justify-center gap-2 p-4 bg-stone-50 border border-stone-200 rounded-2xl hover:bg-stone-100 hover:border-stone-300 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed min-h-[80px]"
  >
    <span className="text-stone-500">{icon}</span>
    <span className="text-stone-700 text-sm font-medium text-center leading-tight">{label}</span>
  </button>
);

const InfoRow: React.FC<{ label: string; value?: string | null; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-stone-100 last:border-0">
    <span className="text-stone-500 text-sm">{label}</span>
    <span className={`text-stone-800 text-sm ${mono ? 'font-mono' : ''}`}>{value || 'N/A'}</span>
  </div>
);

export default TenantProfile;
